// calendarClipboard.js - Kopiera/klistra in bokning (Spår K4, som i PMO)
//
// Urklippet delas av vecko- och dagvyn (dagvyn renderas i stället för
// veckovyn, så komponent-state skulle tappas vid vybyte) och lever i
// minnet under sessionen. Innehåller bara det som behövs för att skapa en
// ny bokning via det vanliga skapa-flödet - aldrig tid, status eller betalning.

import { createAppointment, createGroupAppointment } from './calendarApi';

let copied = null;

const durationMinutes = (apt) => {
  const ms = new Date(apt.end_time).getTime() - new Date(apt.start_time).getTime();
  const min = Math.round(ms / 60000);
  return min > 0 ? min : (apt.appointment_type?.duration_minutes || 30);
};

// Kopiera en bokning (vanlig, materialiserad eller virtuellt tillfälle).
// Gruppbokning: samma grupp/typ/längd + de deltagare som inte avbokats.
export const copyAppointment = (apt) => {
  const participants = apt.is_group && Array.isArray(apt.participants)
    ? apt.participants
      .filter(p => p.status !== 'cancelled')
      .map(p => ({
        patient_id: p.patient_id,
        name: p.patient?.fullname || p.fullname || 'Okänd patient'
      }))
    : [];
  copied = {
    is_group: !!apt.is_group,
    group_name: apt.group_name || '',
    patient_id: apt.patient_id || apt.patient?.patient_id || null,
    patient_name: apt.patient?.fullname || null,
    appointment_type_id: apt.appointment_type_id || apt.appointment_type?.type_id || null,
    type_name: apt.appointment_type?.name || null,
    duration_minutes: durationMinutes(apt),
    participants
  };
  return copied;
};

export const getCopiedAppointment = () => copied;

// Kort etikett för menyn: "Anna Andersson (45 min)" / "Grupp X, 4 deltagare (60 min)"
export const copiedLabel = (c = copied) => {
  if (!c) return '';
  const who = c.is_group
    ? `${c.group_name || 'Gruppbokning'}, ${c.participants.length} deltagare`
    : (c.patient_name || 'Bokning');
  return `${who} (${c.duration_minutes} min)`;
};

// Lokal tid utan tidszon, som resten av kalendern skickar
const toApiTime = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
};

// Klistra in på en tid hos en behandlare via det vanliga skapa-flödet
// (krockkontroll, arbetstid, kalenderspärr, bekräftelseutskick sköts av
// backend). Frågorna (dubbelboka / utanför arbetstid) ställs med confirm
// precis som vid drag. Returnerar den skapade bokningen, eller null om
// användaren avbröt.
export const pasteAppointment = async (startDate, userId) => {
  const c = copied;
  if (!c) return null;

  const timeLabel = startDate.toLocaleString('sv-SE', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  });
  if (c.is_group) {
    const names = c.participants.length
      ? c.participants.map(p => `• ${p.name}`).join('\n')
      : '(inga deltagare)';
    if (!window.confirm(
      `Klistra in grupptiden "${c.group_name || 'Gruppbokning'}" ${timeLabel} `
      + `(${c.duration_minutes} min) med samma deltagare?\n\n${names}`
    )) return null;
  }

  const base = {
    appointment_type_id: c.appointment_type_id,
    start_time: toApiTime(startDate),
    duration_minutes: c.duration_minutes,
    user_id: userId ? Number(userId) : null
  };
  const create = (extra) => (c.is_group
    ? createGroupAppointment({
      ...base,
      group_name: c.group_name || 'Gruppbokning',
      participant_ids: c.participants.map(p => p.patient_id),
      ...extra
    })
    : createAppointment({ ...base, patient_id: c.patient_id, ...extra }));

  let extra = {};
  // Max två frågor (arbetstid + krock) - sedan är båda overrides satta
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await create(extra);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.requires_override && !extra.schedule_override) {
        if (!window.confirm(`${data.error || 'Tiden är utanför behandlarens arbetstid'}.\n\nBoka ändå?`)) return null;
        extra = { ...extra, schedule_override: true };
      } else if (data.requires_conflict_override && !extra.conflict_override) {
        if (!window.confirm(`${data.error || 'Tiden krockar med en annan bokning.'}\n\nVill du dubbelboka ändå?`)) return null;
        extra = { ...extra, conflict_override: true };
      } else {
        throw err;
      }
    }
  }
  return null;
};
